/**
 * What the manifest tree shows on City Satisfaction nodes beyond the manifest
 * itself: a value GUS would ignore (with a way back to GUS's default), where an
 * old `subtabs.*` or `rules.*` switch now lives, when it disagrees with its new
 * home, and the settings panel under `config`.
 *
 * Switch labels come from the tenant's own manifest (GUS fills `label_ar` for
 * every Satisfaction switch); nothing here carries a label of its own for a
 * switch. Everything else is said in English and Arabic.
 */
import React from 'react';
import styled from 'styled-components';
import { spacing, typography, borderRadius, brandColors } from '../../design-system/tokens';
import { FlagManifest, FlagNode } from '../../services/api';
import { Decorate, NodeDecoration, Notice, NoticeTitle, PathBadge } from '../flags/FlagTree';
import SatisfactionSettingsPanel, { PANEL_KEYS } from './SatisfactionSettingsPanel';
import { aliasRoleOf, readAlias } from './satisfactionAliases';
import { SATISFACTION_PATH, SettingError, satisfactionErrors } from './satisfactionSettings';

const Arabic = styled.span`
  display: block;
  margin-top: ${spacing['1']};
`;

const ResetButton = styled.button`
  margin-top: ${spacing['1']};
  padding: ${spacing['1']} ${spacing['2']};
  font-family: inherit;
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
  background: transparent;
  border: 1px solid ${props => props.theme?.colors?.border?.primary || 'rgba(255, 255, 255, 0.1)'};
  border-radius: ${borderRadius.md};
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid ${props => props.theme?.colors?.accent?.primary || brandColors.silaTeal};
    outline-offset: 2px;
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

function indexNodes(nodes: FlagNode[] | undefined, out: Map<string, FlagNode>): Map<string, FlagNode> {
  for (const n of nodes || []) {
    if (n && typeof n.path === 'string') {
      out.set(n.path, n);
      indexNodes(n.children, out);
    }
  }
  return out;
}

const parentOf = (path: string) => path.slice(0, Math.max(0, path.lastIndexOf('.')));
const onOff = (on: boolean) => (on ? 'on' : 'off');
const onOffAr = (on: boolean) => (on ? 'مفعّل' : 'معطّل');

export function satisfactionDecorate(
  manifest: FlagManifest,
  storedFlags: Record<string, any>,
  onChange: (path: string, value: any) => void,
  disabled = false,
): Decorate {
  const byPath = indexNodes(manifest.modules, new Map());
  const full = (rel: string) => `${SATISFACTION_PATH}.${rel}`;
  const name = (rel: string) => {
    const node = byPath.get(full(rel));
    return (
      <>
        {node?.label || rel}
        {node?.label_ar && <span lang="ar" dir="rtl"> ({node.label_ar})</span>}{' '}
        <PathBadge as="code">{rel}</PathBadge>
      </>
    );
  };
  const nameAr = (rel: string) => {
    const node = byPath.get(full(rel));
    return (
      <>
        {node?.label_ar || node?.label || rel} <PathBadge as="code">{rel}</PathBadge>
      </>
    );
  };
  const names = (rels: string[], render: (rel: string) => React.ReactNode, sep: string) =>
    rels.map((rel, i) => <React.Fragment key={rel}>{i > 0 && sep}{render(rel)}</React.Fragment>);

  // A value GUS would ignore is shown on the nearest node the manifest draws, except the
  // settings the panel edits: the panel shows those beside their own editor. A manifest
  // with no config node has no panel, so then the tree shows those too.
  const hasPanel = byPath.has(`${SATISFACTION_PATH}.config`);
  const panelPaths = hasPanel ? PANEL_KEYS.map((k) => `${SATISFACTION_PATH}.config.${k}`) : [];
  const homeOf = (path: string): string => {
    let p = path;
    while (p && !byPath.has(p)) p = parentOf(p);
    return p;
  };
  const errorsAt = new Map<string, SettingError[]>();
  for (const e of satisfactionErrors(storedFlags)) {
    if (panelPaths.some((p) => e.path === p || e.path.startsWith(`${p}.`))) continue;
    const at = homeOf(e.path);
    if (at) errorsAt.set(at, [...(errorsAt.get(at) || []), e]);
  }

  const errorDecoration = (node: FlagNode): NodeDecoration | undefined => {
    const errs = errorsAt.get(node.path);
    if (!errs) return undefined;
    return {
      badges: [{ tone: 'danger', text: 'GUS would ignore a value', textAr: 'سيتجاهل GUS قيمة' }],
      notices: (
        <Notice $tone="danger" role="alert">
          <NoticeTitle>
            GUS would ignore {errs.length === 1 ? 'this value' : 'these values'}{' '}
            <span lang="ar" dir="rtl">(سيتجاهل GUS {errs.length === 1 ? 'هذه القيمة' : 'هذه القيم'})</span>
          </NoticeTitle>
          {errs.map((e) => (
            <div key={`${e.rule}-${e.path}`}>
              <PathBadge as="code">{e.path.slice(SATISFACTION_PATH.length + 1)}</PathBadge> {e.message.en}
              <Arabic lang="ar" dir="rtl">{e.message.ar}</Arabic>
              <ResetButton type="button" disabled={disabled} onClick={() => onChange(e.path, null)}>
                Use GUS default <span lang="ar" dir="rtl">/ استخدم الإعداد الافتراضي في GUS</span>
              </ResetButton>
            </div>
          ))}
        </Notice>
      ),
    };
  };

  const aliasDecoration = (node: FlagNode): NodeDecoration | undefined => {
    const role = aliasRoleOf(node.path);
    if (!role) return undefined;
    const r = readAlias(storedFlags, role.home);

    if (role.role === 'home') {
      const setOld = r.old.filter((o) => o.set);
      const against = setOld.filter((o) => o.on !== r.homeOn).map((o) => o.path);
      return {
        badges: r.disagree
          ? [{ tone: 'warning', text: 'disagrees with an old switch', textAr: 'يخالف مفتاحًا قديمًا' }]
          : undefined,
        notices: (
          <>
            <Notice $tone="info">
              <NoticeTitle>
                Old switches GUS also reads for this{' '}
                <span lang="ar" dir="rtl">(مفاتيح قديمة يقرؤها GUS لهذا أيضًا)</span>
              </NoticeTitle>
              {names(r.old.map((o) => o.path), name, ', ')}
              . GUS reads them only while this switch is not set, and then this is off only when all of them are off.
              {!r.homeDecides && setOld.length > 0 && (
                <> This switch is not set, so the old switches decide: it is {onOff(r.on)}.</>
              )}
              <Arabic lang="ar" dir="rtl">
                {names(r.old.map((o) => o.path), nameAr, '، ')}
                . يقرؤها GUS فقط ما دام هذا المفتاح غير مضبوط، وعندها لا يكون معطّلًا إلا إذا عُطّلت جميعها.
                {!r.homeDecides && setOld.length > 0 && (
                  <> هذا المفتاح غير مضبوط، لذا تحسم المفاتيح القديمة حالته: {onOffAr(r.on)}.</>
                )}
              </Arabic>
            </Notice>
            {r.disagree && (
              <Notice $tone="warning">
                <NoticeTitle>
                  This switch and an old switch disagree{' '}
                  <span lang="ar" dir="rtl">(هذا المفتاح ومفتاح قديم متعارضان)</span>
                </NoticeTitle>
                This switch is {onOff(r.homeOn)}, but {names(against, name, ', ')} is {onOff(!r.homeOn)}. GUS uses
                this switch and ignores the old one. Remove the old switch, or set it to match, so the two do not
                say different things.
                <Arabic lang="ar" dir="rtl">
                  هذا المفتاح {onOffAr(r.homeOn)}، لكن {names(against, nameAr, '، ')} {onOffAr(!r.homeOn)}. يعتمد GUS
                  هذا المفتاح ويتجاهل القديم. احذف المفتاح القديم أو اضبطه ليطابقه، حتى لا يتعارضا.
                </Arabic>
              </Notice>
            )}
          </>
        ),
      };
    }

    const rel = node.path.slice(SATISFACTION_PATH.length + 1);
    const mine = r.old.find((o) => o.path === rel);
    const disagrees = r.homeDecides && !!mine?.set && mine.on !== r.homeOn;
    return {
      badges: [
        { tone: 'neutral', text: 'old switch', textAr: 'مفتاح قديم' },
        ...(disagrees
          ? [{ tone: 'warning' as const, text: 'disagrees with its new switch', textAr: 'يخالف مفتاحه الجديد' }]
          : []),
      ],
      notices: (
        <Notice $tone={disagrees ? 'warning' : 'info'}>
          <NoticeTitle>Old switch. Its new home is {name(role.home)}</NoticeTitle>
          GUS reads this only while that switch is not set. Set the new switch instead, and do not set both.
          {r.homeDecides && <> The new switch is set ({onOff(r.homeOn)}), so GUS ignores this one.</>}
          {disagrees && <> This one says {onOff(!r.homeOn)}, which disagrees with it.</>}
          <Arabic lang="ar" dir="rtl">
            مفتاح قديم. موضعه الجديد هو {nameAr(role.home)}. يقرؤه GUS فقط ما دام ذلك المفتاح غير مضبوط. اضبط المفتاح
            الجديد بدلًا منه، ولا تضبط الاثنين.
            {r.homeDecides && <> المفتاح الجديد مضبوط ({onOffAr(r.homeOn)})، لذا يتجاهل GUS هذا المفتاح.</>}
            {disagrees && <> وهذا المفتاح {onOffAr(!r.homeOn)}، فهو يخالفه.</>}
          </Arabic>
        </Notice>
      ),
    };
  };

  return (node: FlagNode): NodeDecoration | undefined => {
    const parts: NodeDecoration[] = [];
    if (node.path === `${SATISFACTION_PATH}.config`) {
      parts.push({
        declaredKeys: PANEL_KEYS,
        body: <SatisfactionSettingsPanel storedFlags={storedFlags} onChange={onChange} disabled={disabled} />,
      });
    }
    const err = errorDecoration(node);
    const alias = aliasDecoration(node);
    if (err) parts.push(err);
    if (alias) parts.push(alias);
    if (!parts.length) return undefined;
    const badges = parts.flatMap((p) => p.badges || []);
    const notices = parts.filter((p) => p.notices);
    return {
      badges: badges.length ? badges : undefined,
      notices: notices.length
        ? <>{notices.map((p, i) => <React.Fragment key={i}>{p.notices}</React.Fragment>)}</>
        : undefined,
      body: parts.find((p) => p.body)?.body,
      declaredKeys: parts.find((p) => p.declaredKeys)?.declaredKeys,
    };
  };
}
